import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import { getAdminGroupGuid, getCurrentOrgId, getUserGuid, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { hasImplicitAdminAccess } from '../../../../../user-management/users/util';
import wizardStyles from '../../../shared/wizard-modal.module.scss';
import { useNameAvailability } from '../../../use-name-availability';
import { V2TranslationKeys } from '../../constants';
import createWizardStyles from '../create-user-wizard/create-user-wizard.module.scss';
import { StepAssignGroups } from '../create-user-wizard/step-assign-groups';
import { StepCredentials } from '../create-user-wizard/step-credentials';
import styles from './edit-user-wizard.module.scss';
import { useEditUserForm } from './useEditUserForm';
import { canEditUserCredentials, SubmitResult } from './utils';
export const EditUserWizard = ({ isOpen, userId, isClusterAdmin, onClose, onSuccess, }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [selectedOrgIds, setSelectedOrgIds] = useState(new Set());
    const { form, actions, errors, serverFieldErrors, setServerFieldErrors, isLoading, isSubmitting, submitError, validateCredentials, handleSubmit, originalValues, belongToAllOrgs, isFederated, isSystemPrincipal, isSuperUser, groupsData, orgsData, } = useEditUserForm({ userId, isClusterAdmin });
    const federatedNameLock = isOktaEnabled() && isFederated;
    const canEditCredentials = useMemo(() => {
        if (!originalValues)
            return false;
        return canEditUserCredentials({
            userId,
            userOrgIds: originalValues.orgIds,
            belongToAllOrgs,
        });
    }, [userId, originalValues, belongToAllOrgs]);
    const { checkName } = useNameAvailability({
        kind: 'user',
        currentOrgId: getCurrentOrgId(),
        excludeId: userId,
    });
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    useEffect(() => {
        if (isOpen && originalValues) {
            setSelectedGroupIds(originalValues.groupIds);
            setSelectedOrgIds(originalValues.orgIds);
        }
    }, [isOpen, originalValues]);
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setIsCheckingAvailability(false);
        }
    }, [isOpen]);
    const isEditingSelf = userId === getUserGuid();
    const adminGroupLocked = isSystemPrincipal || hasImplicitAdminAccess(isSuperUser, isEditingSelf);
    const adminGroupGuid = getAdminGroupGuid();
    const groupItems = useMemo(() => {
        const items = groupsData.list.map(group => ({
            id: group.id,
            label: group.name,
            disabled: adminGroupLocked && group.id === adminGroupGuid,
        }));
        if (!originalValues)
            return items;
        return items.sort((itemA, itemB) => {
            const pinA = originalValues.groupIds.has(itemA.id) ? 0 : 1;
            const pinB = originalValues.groupIds.has(itemB.id) ? 0 : 1;
            return pinA - pinB;
        });
    }, [groupsData.list, originalValues, adminGroupLocked, adminGroupGuid]);
    const orgItems = useMemo(() => {
        const items = orgsData.list.map(org => ({
            id: org.orgId,
            label: org.orgName,
            disabled: isEditingSelf && org.orgId === '0',
        }));
        if (!originalValues)
            return items;
        return items.sort((itemA, itemB) => {
            const pinA = originalValues.orgIds.has(itemA.id) ? 0 : 1;
            const pinB = originalValues.orgIds.has(itemB.id) ? 0 : 1;
            return pinA - pinB;
        });
    }, [orgsData.list, originalValues, isEditingSelf]);
    const handleFinalSubmit = useCallback(async () => {
        if ((isClusterAdmin || canEditCredentials) && !validateCredentials()) {
            if (isClusterAdmin)
                setCurrentStep(0);
            return;
        }
        const result = await handleSubmit(selectedGroupIds, selectedOrgIds);
        if (result === SubmitResult.SUCCESS)
            onSuccess();
        if (result === SubmitResult.NO_CHANGES)
            onClose();
        if (result === SubmitResult.DUPLICATE_NAME && isClusterAdmin) {
            setCurrentStep(0);
        }
    }, [
        isClusterAdmin,
        canEditCredentials,
        validateCredentials,
        handleSubmit,
        selectedGroupIds,
        selectedOrgIds,
        onSuccess,
        onClose,
    ]);
    const submitErrorBanner = submitError ? (React.createElement(Alert.Banner, { isVisible: true, message: submitError, type: BannerAlertType.ERROR, showCloseButton: false })) : null;
    const handleStep1Next = async () => {
        if (!validateCredentials())
            return;
        const nameUnchanged = !!originalValues && form.name.trim() === originalValues.name.trim();
        if (!nameUnchanged) {
            setIsCheckingAvailability(true);
            const result = await checkName(form.name);
            setIsCheckingAvailability(false);
            if (result.status === 'taken') {
                setServerFieldErrors({ name: result.takenMessage });
                return;
            }
        }
        setCurrentStep(1);
    };
    if (isClusterAdmin) {
        const wizardSteps = [
            {
                stepTitle: t(V2TranslationKeys.EDIT_CREDENTIALS_STEP),
                stepContent: (React.createElement("div", { className: createWizardStyles.stepWithBanner },
                    submitErrorBanner,
                    React.createElement(StepCredentials, { form: form, actions: actions, errors: errors, nameServerError: serverFieldErrors.name, hidePasswordFields: true, disabledFields: {
                            name: federatedNameLock,
                            displayName: federatedNameLock,
                        }, contentTitle: t('users.wizard.contentTitle.editUserCredentials') }))),
                stepForwardButton: {
                    text: t('modal.next'),
                    onNext: handleStep1Next,
                    isDisabled: isSubmitting || isLoading || isCheckingAvailability,
                    isLoading: isCheckingAvailability,
                },
            },
            {
                stepTitle: t('users.wizard.stepTitle.assignOrgs'),
                stepContent: (React.createElement("div", { className: createWizardStyles.stepWithBanner },
                    submitErrorBanner,
                    React.createElement(StepAssignGroups, { groups: groupItems, groupsLoading: groupsData.loading, selectedGroupIds: selectedGroupIds, onGroupSelectionChange: setSelectedGroupIds, groupSearchText: groupsData.searchText, onGroupSearchChange: groupsData.setSearchText, orgs: orgItems, orgsLoading: orgsData.loading, selectedOrgIds: selectedOrgIds, onOrgSelectionChange: setSelectedOrgIds, isClusterAdmin: isClusterAdmin }))),
                stepForwardButton: {
                    text: t(V2TranslationKeys.SAVE),
                    onNext: handleFinalSubmit,
                    isDisabled: isSubmitting,
                    isLoading: isSubmitting,
                },
                stepBackButton: {
                    text: t('roles.wizard.back'),
                    onBack: () => setCurrentStep(0),
                },
            },
        ];
        return (React.createElement(WizardModal, { isOpen: isOpen, size: ModalSize.MEDIUM, title: t(V2TranslationKeys.EDIT_USER_TITLE), onDismiss: onClose, denyButton: {
                text: t(V2TranslationKeys.CANCEL),
                onDeny: onClose,
            }, wizardModalStep: wizardSteps, currentStep: currentStep, isProgressBarEnabled: true, includesHeader: true, includesFooter: true, className: createWizardStyles.createUserWizardModal, customHeaderClassName: wizardStyles.wizardHeader, customStepsClassName: wizardStyles.wizardContent, customFooterClassName: wizardStyles.wizardFooter }));
    }
    return (React.createElement(Modal, { isOpen: isOpen, size: ModalSize.MEDIUM, title: t(V2TranslationKeys.EDIT_USER_TITLE), onDismiss: onClose, includesHeader: true, includesFooter: true, isContentLoading: isLoading, confirmButton: {
            text: t(V2TranslationKeys.SAVE),
            onConfirm: handleFinalSubmit,
            isDisabled: isSubmitting || isLoading,
            isLoading: isSubmitting,
        }, dismissButton: {
            text: t(V2TranslationKeys.CANCEL),
            onClick: onClose,
        }, className: styles.editModal, contentClassName: styles.editModalContent },
        React.createElement("div", { className: createWizardStyles.stepWithBanner },
            submitErrorBanner,
            canEditCredentials && (React.createElement(StepCredentials, { form: form, actions: actions, errors: errors, nameServerError: serverFieldErrors.name, hidePasswordFields: true, disabledFields: {
                    name: federatedNameLock,
                    displayName: federatedNameLock,
                }, contentTitle: t('users.wizard.contentTitle.editUserCredentials') })),
            React.createElement(StepAssignGroups, { groups: groupItems, groupsLoading: groupsData.loading, selectedGroupIds: selectedGroupIds, onGroupSelectionChange: setSelectedGroupIds, groupSearchText: groupsData.searchText, onGroupSearchChange: groupsData.setSearchText, orgs: orgItems, orgsLoading: false, selectedOrgIds: selectedOrgIds, onOrgSelectionChange: setSelectedOrgIds, isClusterAdmin: false }))));
};
//# sourceMappingURL=edit-user-wizard.js.map